import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import {
  PublicOrderReleaseTracking,
  PublicTrackingDelivery,
  PublicTrackingDocument,
  PublicTrackingEvent,
  PublicTrackingLeg,
  TransportOrderReleaseLine
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

@Component({
  selector: 'app-order-release-public-tracking',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    TagModule
  ],
  template: `
    <main class="tracking-page">
      <header class="tracking-header">
        <a class="brand" routerLink="/track" aria-label="LogiLink tracking home">
          <img src="/assets/logilink_logo_color.png" alt="LogiLink">
          <span>LOGILINK</span>
        </a>

        @if (tracking?.owningCompanyLogo || tracking?.owningCompanyName) {
          <div class="owner-brand" aria-label="Owning company">
            @if (tracking?.owningCompanyLogo) {
              <img [src]="tracking?.owningCompanyLogo" [alt]="(tracking?.owningCompanyName || 'Company') + ' logo'">
            } @else {
              <span class="owner-fallback">{{ ownerInitial() }}</span>
            }
            <span>{{ tracking?.owningCompanyName || 'Company' }}</span>
          </div>
        }
      </header>

      <section class="tracking-hero">
        <div class="hero-copy">
          <span class="eyebrow">Order Release Tracking</span>
          <h1>{{ tracking?.trackingNumber || 'Track your order release' }}</h1>
          <p>{{ tracking?.sourceNumber || tracking?.releaseNumber || 'Enter the tracking number shared by your logistics team.' }}</p>
        </div>

        <form class="tracking-search" (ngSubmit)="search()">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-qrcode"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="publicTrackingNumber" name="publicTrackingNumber" [(ngModel)]="trackingInput" autocomplete="off">
              <label for="publicTrackingNumber">Tracking number</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-button type="submit" label="Track" icon="pi pi-search" [loading]="isLoading"></p-button>
        </form>
      </section>

      @if (isLoading) {
        <section class="state-panel">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Loading tracking details</span>
        </section>
      } @else if (errorMessage) {
        <section class="state-panel error">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ errorMessage }}</span>
        </section>
      }

      @if (tracking && !isLoading) {
        <section class="summary-band">
          <div class="status-block">
            <small>Status</small>
            <p-tag [value]="tracking.releaseStatus || tracking.validationStatus || 'PENDING'" [severity]="statusSeverity(tracking.releaseStatus || tracking.validationStatus)"></p-tag>
          </div>
          <div>
            <small>From</small>
            <strong>{{ tracking.originName || 'Not available' }}</strong>
          </div>
          <div>
            <small>To</small>
            <strong>{{ tracking.destinationName || 'Not available' }}</strong>
          </div>
          <div>
            <small>Need by</small>
            <strong>{{ displayDate(tracking.deliveryWindowEnd || tracking.deliveryWindowStart || tracking.pickupWindowStart) }}</strong>
          </div>
          <div>
            <small>Load</small>
            <strong>{{ lineCount() }} line{{ lineCount() === 1 ? '' : 's' }} / {{ totalQuantity() }}</strong>
          </div>
        </section>

        <section class="content-grid">
          <div class="primary-column">
            <section class="tracking-section">
              <div class="section-head">
                <div>
                  <span class="eyebrow">Timeline</span>
                  <h2>Events</h2>
                </div>
                <span class="count-pill">{{ events().length }}</span>
              </div>
              <div class="event-list">
                @for (event of events(); track trackEvent($index, event)) {
                  <article class="event-row">
                    <span class="event-icon"><i [class]="eventIcon(event)"></i></span>
                    <div>
                      <strong>{{ event.title || event.eventType || 'Update' }}</strong>
                      <p>{{ event.description || event.status || event.locationName || 'Status updated' }}</p>
                      <small>
                        {{ displayDate(event.eventAt) }}
                        @if (event.locationName) { <span> / {{ event.locationName }}</span> }
                        @if (event.deliveryNumber) { <span> / {{ event.deliveryNumber }}</span> }
                      </small>
                    </div>
                  </article>
                } @empty {
                  <div class="empty-inline">No shipment events have been recorded yet.</div>
                }
              </div>
            </section>

            <section class="tracking-section">
              <div class="section-head">
                <div>
                  <span class="eyebrow">Delivery</span>
                  <h2>Shipments and Sites</h2>
                </div>
                <span class="count-pill">{{ deliveries().length }}</span>
              </div>
              <div class="delivery-list">
                @for (delivery of deliveries(); track delivery.deliveryId || $index) {
                  <article class="delivery-block">
                    <div class="delivery-head">
                      <div>
                        <strong>{{ delivery.deliveryNumber || 'Delivery pending' }}</strong>
                        <small>{{ delivery.externalReference || delivery.workflowState || delivery.jobStatus || 'Delivery details pending' }}</small>
                      </div>
                      <p-tag [value]="delivery.workflowState || delivery.jobStatus || 'PENDING'" [severity]="statusSeverity(delivery.workflowState || delivery.jobStatus)"></p-tag>
                    </div>
                    <div class="site-list">
                      @for (stop of delivery.stops || []; track stop.stopId || $index) {
                        <div class="site-row">
                          <span>{{ stop.stopNumber || '-' }}</span>
                          <div>
                            <strong>{{ stop.locationName || stop.stopType || 'Site' }}</strong>
                            <small>{{ stop.stopType || 'Stop' }} / {{ stop.workflowState || 'PENDING' }} / {{ stop.evidenceStatus || 'Evidence pending' }}</small>
                            <small>{{ displayDate(stop.actualArrival || stop.scheduledArrival) }}</small>
                          </div>
                        </div>
                      } @empty {
                        <div class="empty-inline">Site stops will appear after the truck plan is applied.</div>
                      }
                    </div>
                  </article>
                } @empty {
                  <div class="empty-inline">Shipment and delivery details are not available yet.</div>
                }
              </div>
            </section>
          </div>

          <aside class="side-column">
            @if (legs().length) {
              <section class="tracking-section">
                <div class="section-head compact">
                  <div>
                    <span class="eyebrow">Cross Dock</span>
                    <h2>Route Legs</h2>
                  </div>
                </div>
                <div class="leg-list">
                  @for (leg of legs(); track leg.releaseId || $index) {
                    <article class="leg-row">
                      <p-tag [value]="legTitle(leg)" severity="info"></p-tag>
                      <strong>{{ leg.originName || 'Origin' }} to {{ leg.destinationName || 'Destination' }}</strong>
                      <small>{{ leg.releaseNumber || '-' }} / {{ leg.customerTrackingNumber || '-' }}</small>
                      <small>{{ legLoadSummary(leg) }}</small>
                    </article>
                  }
                </div>
              </section>
            }

            <section class="tracking-section">
              <div class="section-head compact">
                <div>
                  <span class="eyebrow">Load</span>
                  <h2>Item Lines</h2>
                </div>
              </div>
              <div class="line-list">
                @for (line of lines(); track line.releaseLineId || line.sourceLineId || $index) {
                  <article class="line-row">
                    <strong>{{ lineTitle(line) }}</strong>
                    <small>{{ lineQuantity(line) }}</small>
                    <small>{{ line.itemSize || 'Size not set' }} / {{ line.actionRequired || line.sourceLineType || 'Source line' }}</small>
                  </article>
                } @empty {
                  <div class="empty-inline">Load details are not available.</div>
                }
              </div>
            </section>

            <section class="tracking-section">
              <div class="section-head compact">
                <div>
                  <span class="eyebrow">Documents</span>
                  <h2>Waybill, Evidence and POD</h2>
                </div>
              </div>
              <div class="document-list">
                @for (document of documents(); track document.url || document.name || $index) {
                  <article class="document-row">
                    <div>
                      <p-tag [value]="document.documentType || 'DOCUMENT'" [severity]="documentSeverity(document.documentType)"></p-tag>
                      <strong>{{ document.name || document.documentType || 'Document' }}</strong>
                      <small>{{ displayDate(document.capturedAt) }}</small>
                    </div>
                    <button type="button" class="icon-link" [disabled]="!document.url" (click)="openDocument(document)" aria-label="Open document">
                      <i class="pi pi-external-link"></i>
                    </button>
                  </article>
                } @empty {
                  <div class="empty-inline">Documents will appear when they are uploaded.</div>
                }
              </div>
            </section>
          </aside>
        </section>
      }
    </main>
  `,
  styles: [`
    :host {
      display: block;
      min-height: 100vh;
      background: #f4f7fb;
      color: #172033;
    }

    .tracking-page {
      min-height: 100vh;
    }

    .tracking-header {
      align-items: center;
      background: #ffffff;
      border-bottom: 1px solid #dfe7f3;
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      min-height: 4.5rem;
      padding: 0 5vw;
    }

    .brand {
      align-items: center;
      color: #172033;
      display: inline-flex;
      font-weight: 900;
      gap: 0.6rem;
      letter-spacing: 0;
      text-decoration: none;
    }

    .brand img {
      height: 2.2rem;
      width: auto;
    }

    .owner-brand {
      align-items: center;
      background: #ffffff;
      border: 1px solid #dfe7f3;
      border-radius: 8px;
      color: #172033;
      display: inline-flex;
      font-size: 0.9rem;
      font-weight: 800;
      gap: 0.65rem;
      min-height: 2.5rem;
      max-width: min(24rem, 46vw);
      padding: 0.35rem 0.75rem;
    }

    .owner-brand img {
      display: block;
      max-height: 2rem;
      max-width: 8rem;
      object-fit: contain;
      width: auto;
    }

    .owner-brand span:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .owner-fallback {
      align-items: center;
      background: #e8f7fb;
      border-radius: 999px;
      color: #0f766e;
      display: inline-flex;
      flex: 0 0 auto;
      font-size: 0.82rem;
      font-weight: 900;
      height: 2rem;
      justify-content: center;
      width: 2rem;
    }

    .tracking-hero {
      align-items: end;
      background: #ffffff;
      border-bottom: 1px solid #dfe7f3;
      display: grid;
      gap: 1.25rem;
      grid-template-columns: minmax(0, 1fr) minmax(20rem, 31rem);
      padding: 2.4rem 5vw;
    }

    .eyebrow {
      color: #0f766e;
      display: block;
      font-size: 0.72rem;
      font-weight: 900;
      letter-spacing: 0;
      text-transform: uppercase;
    }

    h1,
    h2,
    p {
      margin: 0;
    }

    h1 {
      font-size: clamp(2rem, 4vw, 4rem);
      line-height: 1.02;
      margin-top: 0.35rem;
    }

    .hero-copy p {
      color: #53657d;
      font-size: 1rem;
      margin-top: 0.55rem;
      max-width: 42rem;
    }

    .tracking-search {
      align-items: stretch;
      display: grid;
      gap: 0.6rem;
      grid-template-columns: minmax(0, 1fr) auto;
    }

    .summary-band {
      display: grid;
      gap: 1px;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      margin: 1rem 5vw 0;
      overflow: hidden;
    }

    .summary-band > div,
    .tracking-section,
    .state-panel {
      background: #ffffff;
      border: 1px solid #dfe7f3;
      border-radius: 8px;
    }

    .summary-band > div {
      border-radius: 0;
      min-height: 5.2rem;
      padding: 0.9rem;
    }

    .summary-band > div:first-child {
      border-bottom-left-radius: 8px;
      border-top-left-radius: 8px;
    }

    .summary-band > div:last-child {
      border-bottom-right-radius: 8px;
      border-top-right-radius: 8px;
    }

    small {
      color: #64748b;
      display: block;
      font-size: 0.78rem;
      line-height: 1.35;
    }

    strong {
      color: #172033;
      display: block;
      line-height: 1.3;
    }

    .status-block {
      display: grid;
      gap: 0.4rem;
      justify-items: start;
    }

    .content-grid {
      display: grid;
      gap: 1rem;
      grid-template-columns: minmax(0, 1fr) minmax(20rem, 27rem);
      padding: 1rem 5vw 3rem;
    }

    .primary-column,
    .side-column,
    .event-list,
    .delivery-list,
    .leg-list,
    .line-list,
    .document-list,
    .site-list {
      display: grid;
      gap: 0.75rem;
    }

    .tracking-section {
      padding: 1rem;
    }

    .section-head {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.9rem;
    }

    .section-head h2 {
      font-size: 1.05rem;
      margin-top: 0.2rem;
    }

    .section-head.compact {
      align-items: start;
    }

    .count-pill {
      background: #e8f7fb;
      border-radius: 999px;
      color: #075985;
      font-size: 0.8rem;
      font-weight: 900;
      padding: 0.3rem 0.65rem;
    }

    .event-row {
      display: grid;
      gap: 0.75rem;
      grid-template-columns: auto minmax(0, 1fr);
      padding: 0.25rem 0 0.75rem;
      position: relative;
    }

    .event-row:not(:last-child)::before {
      background: #dfe7f3;
      bottom: -0.5rem;
      content: "";
      left: 1rem;
      position: absolute;
      top: 2.3rem;
      width: 2px;
    }

    .event-icon {
      align-items: center;
      background: #e8f7fb;
      border: 1px solid #c7ecf3;
      border-radius: 999px;
      color: #0f766e;
      display: inline-flex;
      height: 2rem;
      justify-content: center;
      width: 2rem;
      z-index: 1;
    }

    .event-row p {
      color: #41526a;
      font-size: 0.9rem;
      margin: 0.15rem 0;
    }

    .delivery-block,
    .leg-row,
    .line-row,
    .document-row {
      border: 1px solid #e5edf6;
      border-radius: 8px;
      padding: 0.85rem;
    }

    .delivery-head,
    .document-row {
      align-items: start;
      display: flex;
      gap: 0.75rem;
      justify-content: space-between;
    }

    .site-list {
      border-left: 2px solid #dfe7f3;
      margin-top: 0.8rem;
      padding-left: 0.75rem;
    }

    .site-row {
      display: grid;
      gap: 0.6rem;
      grid-template-columns: 2rem minmax(0, 1fr);
    }

    .site-row > span {
      align-items: center;
      background: #fff7ed;
      border-radius: 999px;
      color: #c2410c;
      display: inline-flex;
      font-weight: 900;
      height: 2rem;
      justify-content: center;
      width: 2rem;
    }

    .leg-row strong,
    .line-row strong {
      display: block;
      margin-bottom: 0.25rem;
    }

    .leg-row small {
      color: #5b6f8d;
      display: block;
      margin-top: 0.2rem;
    }

    .document-row strong {
      margin: 0.45rem 0 0.2rem;
    }

    .icon-link {
      align-items: center;
      background: #f8fafc;
      border: 1px solid #dfe7f3;
      border-radius: 8px;
      color: #0f766e;
      cursor: pointer;
      display: inline-flex;
      height: 2.25rem;
      justify-content: center;
      width: 2.25rem;
    }

    .icon-link:disabled {
      color: #94a3b8;
      cursor: not-allowed;
    }

    .state-panel {
      align-items: center;
      color: #53657d;
      display: flex;
      gap: 0.6rem;
      margin: 1rem 5vw;
      padding: 1rem;
    }

    .state-panel.error {
      background: #fff7f7;
      border-color: #fecdd3;
      color: #be123c;
    }

    .empty-inline {
      color: #64748b;
      font-size: 0.9rem;
      padding: 0.4rem 0;
    }

    :host ::ng-deep p-inputgroup,
    :host ::ng-deep p-iftalabel,
    :host ::ng-deep .p-inputtext {
      width: 100%;
    }

    :host ::ng-deep .p-inputgroupaddon {
      background: #eef6ff;
      color: #49637f;
      min-width: 2.5rem;
    }

    @media (max-width: 920px) {
      .tracking-header {
        padding: 0.7rem 1rem;
      }

      .owner-brand {
        max-width: 52vw;
      }

      .owner-brand img {
        max-width: 6.5rem;
      }

      .tracking-hero,
      .content-grid,
      .summary-band {
        grid-template-columns: 1fr;
      }

      .tracking-search {
        grid-template-columns: 1fr;
      }

      .summary-band > div,
      .summary-band > div:first-child,
      .summary-band > div:last-child {
        border-radius: 8px;
      }
    }
  `]
})
export class OrderReleasePublicTrackingComponent implements OnInit {
  trackingInput = '';
  tracking?: PublicOrderReleaseTracking;
  isLoading = false;
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TransportPlanningService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const trackingNumber = params.get('trackingNumber') || '';
      this.trackingInput = trackingNumber;
      if (trackingNumber) {
        this.load(trackingNumber);
      }
    });
  }

  search(): void {
    const normalized = this.normalizeForUrl(this.trackingInput);
    if (!normalized) {
      this.errorMessage = 'Enter a tracking number.';
      return;
    }
    this.router.navigate(['/track', normalized]);
  }

  lines(): TransportOrderReleaseLine[] {
    return this.tracking?.lines ?? [];
  }

  legs(): PublicTrackingLeg[] {
    return this.tracking?.legs ?? [];
  }

  events(): PublicTrackingEvent[] {
    return this.tracking?.events ?? [];
  }

  deliveries(): PublicTrackingDelivery[] {
    return this.tracking?.deliveries ?? [];
  }

  documents(): PublicTrackingDocument[] {
    return this.tracking?.documents ?? [];
  }

  lineCount(): number {
    return this.lines().length;
  }

  totalQuantity(): string {
    const quantity = this.lines().reduce((sum, line) => sum + Number(line.quantity ?? line.units ?? 0), 0);
    const uoms = Array.from(new Set(this.lines().map(line => line.uom).filter(Boolean)));
    const suffix = uoms.length === 1 ? ` ${uoms[0]}` : ' units';
    return `${this.formatAmount(quantity)}${suffix}`;
  }

  lineTitle(line: TransportOrderReleaseLine): string {
    return [line.itemCode, line.itemName || line.itemDescription]
      .filter(Boolean)
      .join(' - ') || 'Transport demand';
  }

  lineQuantity(line: TransportOrderReleaseLine): string {
    const quantity = Number(line.quantity ?? line.units ?? 0);
    return `${quantity > 0 ? this.formatAmount(quantity) : '-'} ${line.uom || 'unit'}`;
  }

  legTitle(leg: PublicTrackingLeg): string {
    return leg.legSequence && leg.legCount ? `Leg ${leg.legSequence} of ${leg.legCount}` : leg.releaseNumber || 'Leg';
  }

  legLoadSummary(leg: PublicTrackingLeg): string {
    const lines = leg.lines ?? [];
    const quantity = lines.reduce((sum, line) => sum + Number(line.quantity ?? line.units ?? 0), 0);
    const uoms = Array.from(new Set(lines.map(line => line.uom).filter(Boolean)));
    const suffix = uoms.length === 1 ? ` ${uoms[0]}` : ' units';
    return `${this.formatAmount(quantity)}${suffix} / ${lines.length} line${lines.length === 1 ? '' : 's'}`;
  }

  displayDate(value?: string | Date | null): string {
    if (!value) {
      return 'Not available';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Not available';
    }
    return formatDate(date, 'dd MMM yyyy HH:mm', 'en-US');
  }

  statusSeverity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const value = (status || '').toUpperCase();
    if (value.includes('RESCHEDULED')) return 'warn';
    if (value.includes('DELIVER') || value.includes('COMPLETE') || value.includes('RELEASED')) return 'success';
    if (value.includes('ERROR') || value.includes('FAIL') || value.includes('CANCEL')) return 'danger';
    if (value.includes('READY') || value.includes('SCHEDULE') || value.includes('ASSIGN')) return 'info';
    if (value.includes('PENDING') || value.includes('DRAFT')) return 'warn';
    return 'secondary';
  }

  documentSeverity(type?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const value = (type || '').toUpperCase();
    if (value === 'POD') return 'success';
    if (value === 'WAYBILL') return 'info';
    if (value === 'EVIDENCE') return 'warn';
    return 'secondary';
  }

  eventIcon(event: PublicTrackingEvent): string {
    const type = (event.eventType || '').toUpperCase();
    if (type === 'SHIPMENT') return 'pi pi-truck';
    if (type === 'DELIVERY') return 'pi pi-send';
    if (type === 'SITE') return 'pi pi-map-marker';
    if (type === 'EVIDENCE') return 'pi pi-paperclip';
    if (type === 'WORKFLOW') return 'pi pi-sync';
    return 'pi pi-check';
  }

  trackEvent(index: number, event: PublicTrackingEvent): string {
    return [event.eventAt, event.eventType, event.title, index].filter(Boolean).join('|');
  }

  openDocument(document: PublicTrackingDocument): void {
    if (!document.url) {
      return;
    }
    window.open(document.url, '_blank', 'noopener');
  }

  ownerInitial(): string {
    const name = this.tracking?.owningCompanyName?.trim();
    return name ? name.charAt(0).toUpperCase() : 'C';
  }

  private load(trackingNumber: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.service.getPublicOrderReleaseTracking(trackingNumber).subscribe({
      next: tracking => {
        this.tracking = tracking;
        this.trackingInput = tracking?.trackingNumber || trackingNumber;
        this.isLoading = false;
      },
      error: () => {
        this.tracking = undefined;
        this.errorMessage = 'We could not find that tracking number.';
        this.isLoading = false;
      }
    });
  }

  private normalizeForUrl(value: string): string {
    return value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }
}
